/**
 * Signup context: remembers why a visitor is signing up or logging in when
 * they arrive through a club link (/join/{clubID}), an invite link
 * (/invite/...), a club website's sign-in (/site-auth) or, later, a
 * tournament signup link.
 *
 * Consumers:
 *  - ProfileService.initProfile skips the automatic "Club <name>" creation
 *    for a brand-new profile that arrived with a context.
 *  - OnboardingGuard (the /lobby guard) sends an unfinished context back to
 *    its page and clears a finished one.
 *
 * sessionStorage (not memory) because social login on desktop uses
 * signInWithRedirect: the tab leaves and comes back. Per-device UI state
 * never lives in Firebase.
 */
export const SIGNUP_CONTEXT_KEY = "bv_signup_context";
const MAX_AGE_MS = 30 * 60 * 1000;

export interface SignupContext {
  kind: "club" | "tournament" | "site";
  /** Required except for kind "site" (a club website, not a club). */
  clubID?: string;
  tournID?: string;
  /** Route to return to after auth (e.g. "/join/AB12"). */
  path: string;
  at: number;
  /** Set once the page's flow completed (joined, request sent, ...). */
  done?: boolean;
}

export function setSignupContext(
  ctx: Omit<SignupContext, "at" | "done">
): void {
  try {
    const value: SignupContext = { ...ctx, at: Date.now() };
    sessionStorage.setItem(SIGNUP_CONTEXT_KEY, JSON.stringify(value));
  } catch {
    // storage unavailable (private mode, quota) — the flow still works,
    // the user just gets the default post-signup behaviour
  }
}

export function readSignupContext(): SignupContext | null {
  try {
    const raw = sessionStorage.getItem(SIGNUP_CONTEXT_KEY);
    if (!raw) return null;
    const ctx = JSON.parse(raw) as SignupContext;
    if (!ctx || !ctx.kind || !ctx.path) return null;
    if (ctx.kind !== "site" && !ctx.clubID) return null;
    if (!ctx.at || Date.now() - ctx.at > MAX_AGE_MS) {
      clearSignupContext();
      return null;
    }
    return ctx;
  } catch {
    return null;
  }
}

export function markSignupContextDone(): void {
  const ctx = readSignupContext();
  if (!ctx) return;
  try {
    sessionStorage.setItem(
      SIGNUP_CONTEXT_KEY,
      JSON.stringify({ ...ctx, done: true })
    );
  } catch {
    // ignore
  }
}

export function clearSignupContext(): void {
  try {
    sessionStorage.removeItem(SIGNUP_CONTEXT_KEY);
  } catch {
    // ignore
  }
}
